import React, { useEffect, useRef } from 'react'
import { Button, Form } from 'rsuite'
import Icon from './Icon'

// redux
import { useSelector, useDispatch } from 'react-redux'
import { getKioskOptions } from '../redux/options/selectors'
import { setKioskOption } from '../redux/options/actions'

// components
import LabeledToggle from './LabeledToggle'
import StepSlider from './StepSlider'
import RaceOptions from './RaceOptions'
import Credits from './Credits'

// tools
import { ZOOM, OPTION_CHARTS, OPTION_WEATHER, OPTION_ZOOM, OPTION_FIT, getZoom, getShowCharts, getShowWeather, getAutoFit, formatZoom } from '../tools/Kiosk'

/**
 * Clicks inside these stay in the options: the options themselves, the kiosk buttons, which open
 * and close them, and the menus of their pickers, which open outside them
 * @type {string}
 */
const INSIDE = '.kiosk-options, .kiosk-controls, .rs-picker-popup'

/**
 * Get a function that sets the zoom by hand, which stops fitting the groups to the screen by itself
 * @returns {Function} called with the new zoom
 */
export const useManualZoom = () =>
{
  const dispatch = useDispatch()
  return ( zoom ) =>
  {
    dispatch( setKioskOption( OPTION_FIT, false ) )
    dispatch( setKioskOption( OPTION_ZOOM, zoom ) )
  }
}

/**
 * Options of the kiosk screen, kept on the device that shows it, in a panel over the screen just
 * above the kiosk buttons; it closes with its close button, Escape or a click elsewhere
 * @param {Object} props component props
 * @param {boolean} props.show whether the panel is open
 * @param {Function} props.onClose called when the panel is closed
 * @returns {JSX.Element|null} panel, nothing while closed
 */
const KioskOptions = ( { show = false, onClose } ) =>
{
  const options = useSelector( getKioskOptions )
  const dispatch = useDispatch()
  const setZoom = useManualZoom()

  // the latest close function for the listeners, which are bound once per opening
  const onCloseRef = useRef( onClose )
  onCloseRef.current = onClose

  /**
   * Store a kiosk option
   * @param {string} key option name
   * @param {boolean|number} value new value
   * @returns {void}
   */
  const onChange = ( key, value ) => dispatch( setKioskOption( key, value ) )

  // close with Escape or a click elsewhere while open
  useEffect( () =>
  {
    if ( !show ) return

    /**
     * Close on a click outside the options
     * @param {MouseEvent} event pointer event
     * @returns {void}
     */
    const onPointer = ( event ) =>
    {
      if ( event.target instanceof Element && event.target.closest( INSIDE ) ) return
      onCloseRef.current()
    }

    /**
     * Close on Escape
     * @param {KeyboardEvent} event key event
     * @returns {void}
     */
    const onKey = ( event ) =>
    {
      if ( event.key === 'Escape' ) onCloseRef.current()
    }

    document.addEventListener( 'pointerdown', onPointer )
    document.addEventListener( 'keydown', onKey )
    return () =>
    {
      document.removeEventListener( 'pointerdown', onPointer )
      document.removeEventListener( 'keydown', onKey )
    }
  }, [ show ] )

  if ( !show ) return null

  return (
    <div className="kiosk-options" role="dialog" aria-label="Kiosk options">
      <div className="kiosk-options-header">
        <span className="kiosk-options-title"><Icon icon="cog"/> Kiosk options</span>
        <Button
          appearance="subtle"
          aria-label="Close"
          title="Close"
          onClick={ onClose }
        ><Icon icon="close"/></Button>
      </div>
      <Form className="options-form">
        <RaceOptions onClose={ onClose }/>
        <Form.Group className="display">
          <Form.ControlLabel><Icon icon="eye"/> Display charts &amp; graphs</Form.ControlLabel>
          <LabeledToggle
            onChange={ onChange }
            checked={ getShowCharts( options ) }
            name={ OPTION_CHARTS }>Show position chart per group</LabeledToggle>
          <LabeledToggle
            onChange={ onChange }
            checked={ getShowWeather( options ) }
            name={ OPTION_WEATHER }>Show weather and forecast</LabeledToggle>
        </Form.Group>
        <Form.Group className="zoom">
          <Form.ControlLabel><Icon icon="eye"/> Zoom</Form.ControlLabel>
          { /* fitting by itself stops when the zoom is set by hand */ }
          <LabeledToggle
            onChange={ onChange }
            checked={ getAutoFit( options ) }
            name={ OPTION_FIT }>Zoom to fit the screen</LabeledToggle>
          <StepSlider
            value={ getZoom( options ) }
            min={ ZOOM.min }
            max={ ZOOM.max }
            step={ ZOOM.step }
            label="zoom"
            format={ formatZoom }
            onChange={ setZoom }
          />
        </Form.Group>
        { /* a group of its own, for the line above it like between the other groups */ }
        <Form.Group className="credits">
          <Credits/>
        </Form.Group>
      </Form>
    </div>
  )
}

export default KioskOptions
